import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import { useLocation } from 'react-router-dom' 
import FormTabHeader, { TabOption } from '../../../shared/components/FormTabHeader'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import PhoneInput from '../../../shared/components/PhoneInput'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import { showErrorToast, showSuccessToast } from '../../../shared/components/ToastUtils'
import { ToastContainer } from 'react-toastify'
import {
  branches,
  currencies,
  fetchAsyncCarData,
  fetchAsyncCustomerData,
  fetchAsyncReservationSourceData,
  personels,
  tariffs,
} from '../../../shared/models/SharedApiLookups'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import {
  AsyncCarModel,
  AsyncCustomerModel,
  AsyncReservationSourceModel,
} from '../../../shared/models/ApiModels'
import {
  BILL_PERIOD,
  BILL_PROFILE,
  CALCULATION_TYPES, 
  COUNTRY_OPTIONS,
  CURRENCIES,
  DELIVERY_TYPES,
  FUEL_LEVEL,
  getSelectLabel,
  OPTION_TYPES,
  PAYMENT_OWNER,
  PAYMENT_TYPES,
  RENT_STATUS,
  RENTAL_TYPES,
  RETURN_REASON,
  RISK_LEVEL,
} from '../../../shared/models/SharedConstantLookups'
import DateInput from '../../../shared/components/DateInput'
import CheckboxInput from '../../../shared/components/CheckboxInput'
import SubTabHeader from '../../../shared/components/SubTabHeader'
import { CustomerCreateModal } from '../../../shared/modals/CustomerCreateModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../../setup'
import { IPersonnelModel } from '../definition/personnel/PersonnelModel'
import { getPersonnelByUserId } from '../definition/personnel/Api'
import { initialRent, IRentModel, rentSubTabOptions, validationRent } from './RentModel'
import { createRent, getRentById, updateRent } from './Api'
import AdditionalServicesCalculator from '../reservation/AdditionalServicesCalculator'
import FormWatcher from '../reservation/FormWatcher'
import {
  ADDITIONAL_SERVICE_STATUS,
  PRODUCT_OWNER,
} from '../reservation/ReservationModel'
import { Button } from 'react-bootstrap-v5'
import CalculateRentalPrice from '../reservation/CalculateRentalPrice'
import { formatDate, formatExpectedDateTime } from '../../utils/DateUtil'
import { TabNavigationButtons } from '../../../shared/components/TabNavigationButtons'
import { getCustomerById } from '../customer/Api' 
import { getCarDefinitionById } from '../car/Api'
import LoadingOverlay from '../../../shared/components/LoadingOverlay' 

export const RentPage: FC = () => {
  const [tab, setTab] = useState<Tabs>('quick')
  const [tabSub, setTabSub] = useState<string | 'desc'>('desc')
  const [loading, setLoading] = useState(false) 
  const [isEditMode, setIsEditMode] = useState(false)
  const location = useLocation()
  const rentIdFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get('rentId')?.trim() || ''
  }, [location.search])
  const [selectedId, setSelectedId] = useState<string | null>(rentIdFromUrl || null)
  const [initialFormValues, setInitialFormValues] = useState<IRentModel>(initialRent)
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([])
  const [tariffOptions, setTariffOptions] = useState<{ value: string; label: string }[]>([])
  const [currencyOptions, setCurrencyOptions] = useState<{ value: string; label: string }[]>([])
  const [personelOptions, setPersonelOptions] = useState<{ value: string; label: string }[]>([])
  const [remainingAmount, setRemainingAmount] = useState<number | 0>(0)
  const [currency, setCurrency] = useState<string | 'TL'>('TL')
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [isSelectedResSource, setIsSelectedResSource] = useState(false)
  const [isManualPrice, setIsManualPrice] = useState(true)
  const [personnel, setPersonnel] = useState<IPersonnelModel | null>(null)
  const formikRef = useRef<any>(null)
  type Tabs = 'quick' | 'res' | 'customer' | 'car' | 'price' | 'extra' | 'details' | 'return'

  const tabOptions: TabOption<Tabs>[] = [
    { key: 'quick', title: 'Hızlı Giriş', icon: 'bi bi-lightning' },
    { key: 'res', title: 'Kira Bilgisi/Teslimat', icon: 'bi bi-clipboard-check' },
    { key: 'customer', title: 'Müşteri', icon: 'bi bi-person' },
    { key: 'car', title: 'Araç', icon: 'bi bi-car-front' },
    { key: 'price', title: 'Fiyat/Toplam', icon: 'bi bi-cash-coin' },
    { key: 'extra', title: 'Ek Hizmet', icon: 'bi bi-plus-circle' },
    { key: 'details', title: 'Ayrıntılar', icon: 'bi bi-list-ul' },
    { key: 'return', title: 'Dönüş', icon: 'bi bi-list-ul' },
  ]

  const user = useSelector((state: RootState) => state.auth.user)
  const userId = user?.id ?? ''
  useEffect(() => {
    if (!userId) return

    const loadData = async () => {
      const [brn, cr, pr, tr, personnelRes] = await Promise.all([
        branches(),
        currencies(),
        personels(),
        tariffs(),
        getPersonnelByUserId(userId),
      ])
      setBranchOptions(brn)
      setTariffOptions(tr)
      setCurrencyOptions(cr)
      setPersonelOptions(pr)
      const personnel = personnelRes.data?.payload
      if (personnel) {
        setPersonnel(personnel) 
      }
    }

    loadData()
  }, [userId])
  
  useEffect(() => {
    if (!personnel?.branch || branchOptions.length === 0) return
    // Mevcut bir rezervasyonu görüntülerken/düzenlerken personelin
    // varsayılan şubesini formdaki değerlerin üzerine yazmıyoruz
    if (rentIdFromUrl || selectedId) return
    const selected = branchOptions.find((o) => String(o.value) === String(personnel.branch))
    formikRef.current?.setFieldValue('pickupBranch', personnel.branch)
    formikRef.current?.setFieldValue('pickupBranchId', personnel.branch)
    formikRef.current?.setFieldValue('pickupBranchName', selected?.label ?? null)
    formikRef.current?.setFieldValue('pickupBranchAdd', selected?.label ?? null)
    formikRef.current?.setFieldValue('pickupBranchReturn', selected?.label ?? null)
    formikRef.current?.setFieldValue('returnBranch', personnel.branch)
    formikRef.current?.setFieldValue('returnBranchId', personnel.branch)
    formikRef.current?.setFieldValue('returnBranchName', selected?.label ?? null)
    formikRef.current?.setFieldValue('returnBranchAdd', selected?.label ?? null)
    formikRef.current?.setFieldValue('returnBranchReturn', selected?.label ?? null)
    formikRef.current?.setFieldValue('operationBranch', personnel.branch)
  }, [personnel, branchOptions, rentIdFromUrl, selectedId])

  // URL'den rentId geldiğinde kira sözleşmesini yükle ve formu doldur
  useEffect(() => {
    if (!rentIdFromUrl) return
    let cancelled = false
    setLoading(true)
    setIsEditMode(true)
    const run = async () => {
      try {
        const res = await getRentById(rentIdFromUrl)
        if (cancelled) return
        if (res.data.hasError || !res.data.payload) {
          showErrorToast(res.data.message || 'Kira bilgisi alınamadı')
          return
        }
        const rent = res.data.payload
        setSelectedId(rent.id ?? rentIdFromUrl)
        setInitialFormValues({ ...initialRent, ...rent })
        await loadRentForm(rent)
      } catch (err) {
        if (cancelled) return
        const e = err as { response?: { status?: number } }
        if (e?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Kira bilgisi yüklenemedi')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [rentIdFromUrl])

  const loadRentForm = async (rent: IRentModel) => {
    if (!rent) return
    const customer = await getCustomerById(rent.customerId)  
    const car = await getCarDefinitionById(rent.carId) 
    syncCustomerFields(customer, formikRef.current?.setFieldValue)
    syncCarFields(car.carDefinitionDTO, formikRef.current?.setFieldValue) 
    syncCalculationType(rent.calculationType, formikRef.current?.setFieldValue)
    syncDeliveryType(rent.deliveryType, formikRef.current?.setFieldValue) 
    syncExpectedDate(rent.endDate, rent.endTime, formikRef.current?.setFieldValue)
    syncStartDate('endDate', rent.endDate, formikRef.current?.setFieldValue) 
    syncReservationTime('endTime', rent.endTime, formikRef.current?.setFieldValue) 
    syncOutFuelLevel('outFuelLevel', rent.outFuelLevel, formikRef.current?.setFieldValue)
    syncBranchFields('pickupBranch',{value: rent.pickupBranchId, label: rent.pickupBranchName} ,formikRef.current?.setFieldValue)
    syncBranchFields('returnBranch',{value: rent.returnBranchId, label: rent.returnBranchName} ,formikRef.current?.setFieldValue)
  }

  useEffect(() => {
    const setInitialExpectedDate = () => {
      const values = formikRef.current?.values
      if (!values) return false
      const formatted = (() => {
        const d = values.endDate
        if (!d) return ''
        const dateObj = d instanceof Date ? d : new Date(d)
        if (isNaN(dateObj.getTime())) return ''
        const datePart = dateObj.toLocaleDateString('tr-TR')
        const timePart = typeof values.endTime === 'string' ? values.endTime : ''
        return timePart ? `${datePart} ${timePart}` : datePart
      })()
      formikRef.current?.setFieldValue('expectedDate', formatted)
      formikRef.current?.setFieldValue('returnDateText', formatDate(values.endDate))
      formikRef.current?.setFieldValue('returnTimeText', values.endTime)
      formikRef.current?.setFieldValue('customerFullName', values.customerFullName)
      return true
    }
    if (!setInitialExpectedDate()) {
      const t = setTimeout(setInitialExpectedDate, 0)
      return () => clearTimeout(t)
    }
  }, [])

  const handleSave = async (values: IRentModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateRent(selectedId, values)
        showSuccessToast('Sözleşme başarıyla güncellendi')
      } else {
        values.notes = [
          {
            note: values.explanation ?? null,
          },
          {
            note: values.warningDescription ?? null,
          },
          {
            note: values.billDescription ?? null,
          }, 
        ]
        await createRent(values)
        showSuccessToast('Sözleşme başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null)
      setTab('quick')
      setTabSub('desc')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const syncCarFields = (car: AsyncCarModel | null, setFieldValue: any) => {
    console.log('syncCarFields car:', car)
    if (!car?.id) {
      ;[
        'car',
        'car1',
        'brandName',
        'brandNameDetail',
        'modelType',
        'modelTypeDetail',
        'modelYear',
        'transmission',
        'transmissionDetail',
        'fuelType',
        'fuelTypeDetail',
      ].forEach((f) => setFieldValue(f, ''))

      return
    }
    console.log('car:', car)
    setFieldValue('car', car)
    setFieldValue('carId', car.id)
    setFieldValue('carPlate', car.plate)
    setFieldValue('plateAdd', car.plate)
    setFieldValue('plateReturn', car.plate)
    setFieldValue('carDetail', car)

    setFieldValue('brandName', car.brand)
    setFieldValue('brandNameDetail', car.brand)

    setFieldValue('modelType', car.modelType)
    setFieldValue('modelTypeDetail', car.modelType)

    setFieldValue('modelYear', car.modelYear)

    setFieldValue('transmission', car.transmission)
    setFieldValue('transmissionDetail', car.transmission)

    setFieldValue('fuelType', car.fuelType)
    setFieldValue('fuelTypeDetail', car.fuelType)

    setFieldValue('vehicleGroupName', car.vehicleGroup)
    setFieldValue('vehicleGroupNameDetail', car.vehicleGroup)
    setFieldValue('vehicleGroupAdd', car.vehicleGroup)
    setFieldValue('vehicleGroupReturn', car.vehicleGroup)
    setFieldValue('vehicleGroupId', car.vehicleGroupId)
  }

  const syncExpectedDate = (
    endDate: Date | null | undefined,
    endTime: string | null | undefined,
    setFieldValue: any
  ) => {
    setFieldValue('expectedDate', formatExpectedDateTime(endDate, endTime))
    setFieldValue('returnDateText', formatDate(endDate))
    setFieldValue('returnTimeText', endTime)
  }

  const syncStartDate = (
    source: 'startDate' | 'startDateDetail' | 'endDate' | 'endDateDetail',
    value: Date | null,
    setFieldValue: any
  ) => {
    if (source === 'startDate') {
      setFieldValue('startDateDetail', value)
      setFieldValue('startDateAdd', value)
      setFieldValue('startDateReturn', value)
    } else if (source === 'startDateDetail') {
      setFieldValue('startDate', value)
      setFieldValue('startDateAdd', value)
      setFieldValue('startDateReturn', value)
    } else if (source === 'endDate') {
      setFieldValue('endDateDetail', value)
      setFieldValue('endDateAdd', value)
      setFieldValue('endDateReturn', value)
    } else if (source === 'endDateDetail') {
      setFieldValue('endDate', value)
      setFieldValue('endDateAdd', value)
      setFieldValue('endDateReturn', value)
    }
    if (source === 'endDate' || source === 'endDateDetail') {
      syncExpectedDate(value, formikRef.current?.values?.endTime, setFieldValue)
    }
  }

  const syncBranchFields = (
    source: 'pickupBranch' | 'returnBranch',
    value: { value: string; label: string } | null,
    setFieldValue: any
  ) => { 
    setFieldValue(`${source}Name`, value?.label ?? null)
    setFieldValue(`${source}Add`, value?.label ?? null)
    setFieldValue(`${source}Return`, value?.label ?? null)
  }

  const syncReservationTime = (
    source: 'startTime' | 'startTimeDetail' | 'endTime' | 'endTimeDetail',
    value: string | null,
    setFieldValue: any
  ) => {
    if (source === 'startTime') {
      setFieldValue('startTimeDetail', value)
      setFieldValue('startTimeAdd', value)
      setFieldValue('startTimeReturn', value)
    } else if (source === 'startTimeDetail') {
      setFieldValue('startTime', value)
      setFieldValue('startTimeAdd', value)
      setFieldValue('startTimeReturn', value)
    } else if (source === 'endTime') {
      setFieldValue('endTimeDetail', value)
      setFieldValue('endTimeAdd', value)
      setFieldValue('endTimeReturn', value)
    } else if (source === 'endTimeDetail') {
      setFieldValue('endTime', value)
      setFieldValue('endTimeAdd', value)
      setFieldValue('endTimeReturn', value)
    }
    if (
      source === 'endTime' ||
      source === 'endTimeDetail' ||
      source === 'startTime' ||
      source === 'startTimeDetail'
    ) {
      if (source === 'endTime' || source === 'endTimeDetail') {
        syncExpectedDate(formikRef.current?.values?.endDate, value, setFieldValue)
      }
    }
  }

  const parseDate = (value?: string | Date | null): Date | null => {
    if (!value) return null

    if (value instanceof Date) return value

    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  const setDriverFields = (
    source: 'driverFirst' | 'driverSecond',
    driver: AsyncCustomerModel | null,
    setFieldValue: any
  ) => {
    const prefix = source

    setFieldValue(`${prefix}.phone`, driver?.phone ?? '')
    setFieldValue(`${prefix}.tckn`, driver?.tcKimlikNo ?? '')
    setFieldValue(`${prefix}.licenseNo`, driver?.drivingLicenseNo ?? '')
    setFieldValue(`${prefix}.name`, driver?.firstName ?? '')
    setFieldValue(`${prefix}.surname`, driver?.lastName ?? '')
    setFieldValue(`${prefix}.licensePlace`, driver?.drivingLicenseIssuedBy ?? '')

    setFieldValue(`${prefix}.licenseDate`, parseDate(driver?.drivingLicenseIssueDate))

    setFieldValue(`${prefix}.birthDate`, parseDate(driver?.dateOfBirth))
  }

  const syncCustomerFields = (customer: AsyncCustomerModel | null, setFieldValue: any) => {
    if (!customer?.id) {
      ;[
        'customer',
        'customer1',
        'phone',
        'email',
        'tcIdentityNo',
        'birthday',
        'birthPlace',
        'driverLicenseNo',
        'licensePlace',
        'licenseDate',
        'district',
        'city',
        'country',
        'address',
        'passportNo',
        'passportPlace',
        'totalRentalCount',
      ].forEach((f) => setFieldValue(f, ''))
      return
    }
    setFieldValue('customer', customer)
    setFieldValue('customer1', customer)
    setFieldValue('customerId', customer ? customer.id : null)
    setFieldValue(
      'customerFullName',
      customer ? customer.firstName + ' ' + customer.lastName : null
    )
    setFieldValue('customerAdd', customer ? customer.firstName + ' ' + customer.lastName : null)
    setFieldValue('customerReturn', customer ? customer.firstName + ' ' + customer.lastName : null)
    setFieldValue('phone', customer.phone)
    setFieldValue('email', customer.emailAddress)
    setFieldValue('tcIdentityNo', customer.tcKimlikNo)
    setFieldValue('birthday', customer.dateOfBirth)
    setFieldValue('birthPlace', customer.placeOfBirth)
    setFieldValue('driverLicenseNo', customer.drivingLicenseNo)
    setFieldValue('licensePlace', customer.drivingLicenseIssuedBy)
    setFieldValue('licenseDate', customer.drivingLicenseIssueDate)
    setFieldValue('district', customer.district)
    setFieldValue('city', customer.city)
    setFieldValue('country', getSelectLabel(COUNTRY_OPTIONS, customer.licenseCountry))
    setFieldValue('address', customer.address)
    setFieldValue('passportNo', customer.passportNumber)
    setFieldValue('passportPlace', customer.passportIssuedBy)
    setFieldValue('totalRentalCount', customer.totalRentalCount)
  }

  const syncCurrency = (
    source: 'currency' | 'currency1' | 'remainingCurrency' | 'remainingCurrency1',
    value: string | '',
    setFieldValue: any
  ) => {
    const map = {
      currency: 'currency1',
      currency1: 'currency',
      currencyAdd: 'currency',
      generalCurrencyAdd: 'currency',
      remainingCurrency: 'remainingCurrency1',
      remainingCurrency1: 'remainingCurrency',
    } as const

    setFieldValue(map[source], value)
    const currency = CURRENCIES.find((t) => t.value === String(value))?.label ?? ''
    setCurrency(currency)
  }

  const syncReservationSource = async (
    rSource: AsyncReservationSourceModel | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue('reservationSource', rSource)
    setFieldValue('reservationSourceDetail', rSource)
    setFieldValue('reservationSourceId', rSource?.id)
    setFieldValue('reservationSourceName', rSource?.reservationSourceName)
    if (rSource != null) {
      setIsSelectedResSource(false)
      setIsManualPrice(false)
    } else {
      setIsSelectedResSource(false)
      setIsManualPrice(true)
      resetCalculateValues(setFieldValue)
    }
  }

  const resetCalculateValues = (setFieldValue: (field: string, value: any) => void) => {
    setFieldValue('price', 0)
    setFieldValue('price1', 0)
    setFieldValue('priceAdd', 0)
    setFieldValue('priceReturn', 0)
    setFieldValue('discount1', 0)
    setFieldValue('totalAmount', 0)
    setFieldValue('subTotalAmount', 0)
    setFieldValue('totalAmount1', 0)
    setFieldValue('totalAmountAdd', 0)
    setFieldValue('totalAmountReturn', 0)
    setFieldValue('rentalAmount', 0)
    setFieldValue('rentAmount1', 0)
    setFieldValue('taxAmount', 0)
    setFieldValue('kdvTl1', 0)
    setFieldValue('generalTotalWithCurrency', 0)
    setFieldValue('generalTotalWithCurrencyAdd', 0)
    setFieldValue('generalTotalWithCurrencyReturn', 0)
    setFieldValue('grandTotal', 0)
    setFieldValue('priceAfterDiscount', 0)
    setFieldValue('priceAfterDiscount1', 0)
    setFieldValue('remainingAmount', 0)
    setFieldValue('remaining1', 0)
    setFieldValue('remainingAdd', 0)
    setFieldValue('remainingCurrency', 'TL')
    setFieldValue('remainingReturn', 0)
    setFieldValue('currencyReturn', 'TL')
    setRemainingAmount(0)
  }

  const syncCalculationType = (type: string, setFieldValue: any) => {
    setFieldValue('calculationType', type)
    setFieldValue('pricingType', type)
    setFieldValue('calculateTypeAdd', type)
    setFieldValue('calculateTypeReturn', type)
  }

  const syncDeliveryType = (type: string, setFieldValue: any) => {
    setFieldValue('deliveryType', type)
    setFieldValue('deliveryTypeReturn', type)
  }

  const syncOutKM = (
    source: 'outKM' | 'outKmDetail',
    value: string | null,
    setFieldValue: any
  ) => {
    if (source === 'outKM') {
      setFieldValue('outKmDetail', value)
    } else if (source === 'outKmDetail') {
      setFieldValue('outKM', value)
    }
    setFieldValue('outKMReturn', value)
  }

  const calculateUseKM = (outKm: number, inKM: number, setFieldValue: any) => {
    const useKM = inKM - outKm
    if (useKM < 0) {
      setFieldValue('useKM', 0)
      return
    }
    setFieldValue('useKM', useKM)
  }

  const syncOutFuelLevel = (
    source: 'outFuelLevel' | 'outFuelLevelDetail',
    value: string | number,
    setFieldValue: any
  ) => {
    if (source === 'outFuelLevel') {
      setFieldValue('outFuelLevelDetail', value)
    } else if (source === 'outFuelLevelDetail') {
      setFieldValue('outFuelLevel', value)
    }
    setFieldValue('outFuelLevelReturn', value)
  }

  const [selectedServicesTotal, setSelectedServicesTotal] = useState(0)

  const next = (to: Tabs) => setTab(to)
  const back = (to: Tabs) => setTab(to)

  return (
    <> 
      <LoadingOverlay
        show={loading}
        description={
          isEditMode ? 'Kira bilgileri getiriliyor' : 'Sözleşme kaydediliyor'
        }
      /> 

      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        enableReinitialize
        validationSchema={validationRent}
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => { 
            formik.resetForm() 
            setSelectedId(null)
            setTab('quick')
            setTabSub('desc') 
            setIsEditMode(false) 
            setIsManualPrice(true) 
          }

          return (
            <>
              <AdditionalServicesCalculator
                formik={formik}
                setSelectedServicesTotal={setSelectedServicesTotal}
              />
              <FormWatcher />
              <CalculateRentalPrice
                setRemainingAmount={setRemainingAmount}
                addinionalServiceAmount={selectedServicesTotal}
                isManualPrice={isManualPrice}
              />
              <FormTabHeader
                formId='rentForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab as React.Dispatch<React.SetStateAction<string>>}
                setSelectedId={() => { }}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
                smallButton={true}
              />

              <div className='card mb-5'>
                <Form id='rentForm' className='form card-body'>
                  {tab === 'quick' && (
                    <>
                      <div className='row mb-2'>
                        {isEditMode ? (
                          <TextInput
                            name='customerFullName'
                            label='Müşteri Adı Soyadı'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        ) : (
                          <AsyncSearchInput<AsyncCustomerModel>
                            name='customer'
                            label='Cari Ara'
                            placeholder='Ad Soyad girin...'
                            fetchHandler={fetchAsyncCustomerData}
                            displayField={(item) => `${item.firstName} ${item.lastName}`}
                            onSelect={(item) => syncCustomerFields(item, formik.setFieldValue)}
                            onClear={() => syncCustomerFields(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                            modalId='modal_customer_search'
                            showModal={showCustomerModal}
                            onModalToggle={setShowCustomerModal}
                          />
                        )}
                        <SelectInput
                          name='status'
                          label='Durum'
                          wrapperClassName='col-md-3'
                          options={RENT_STATUS}
                          disabled={isEditMode}
                        />
                        <DateInput
                          name='rentDate'
                          type='date'
                          label='Kira Tarihi'
                          wrapperClassName='col-md-3'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <DateInput
                          name='startDate'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('startDate', date, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='startTime'
                          type='time'
                          label='Başlangıç Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('startTime', time, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <div className='col-md-1'></div>
                        <TextInput
                          name='documentNo'
                          label='Sözleşme No'
                          wrapperClassName='col-md-3'
                          readOnly={isEditMode}
                        />
                        <TextInput name='agreementNo' label='Dosya No' wrapperClassName='col-md-3'
                          readOnly={isEditMode} />
                      </div>
                      <div className='row mb-2'>
                        <DateInput
                          name='endDate'
                          type='date'
                          label='Beklenen Tarih'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('endDate', date, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='endTime'
                          type='time'
                          label='Beklenen Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('endTime', time, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='rentalDay'
                          label='Gün'
                          readOnly={true}
                          wrapperClassName='col-md-1'
                          disabled={isEditMode}
                        />
                        <SelectInput
                          name='rentalType'
                          label='Kiralama Türü'
                          wrapperClassName='col-md-3'
                          options={RENTAL_TYPES}
                          disabled={isEditMode}
                        />
                        <SelectInput
                          name='billingType'
                          label='Faturalama'
                          wrapperClassName='col-md-3'
                          options={PAYMENT_OWNER}
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='expectedDate'
                          label='Beklenen Tarih/Saat'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncCustomerModel>
                            name='customerCompany'
                            label='Müşterinin Firması'
                            placeholder='Firma adı girin...'
                            fetchHandler={fetchAsyncCustomerData}
                            displayField={(item) => `${item.firstName} ${item.lastName}`}
                            onSelect={(item) => {
                              formik.setFieldValue('customerCompanyId', item.id);
                              formik.setFieldValue('customerCompanyName', item.firstName + ' ' + item.lastName)
                            }}
                            onClear={() => {
                              formik.setFieldValue('customerCompanyId', '');
                              formik.setFieldValue('customerCompanyName', '')
                            }}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='customerCompanyName'
                            label='Müşterinin Firması'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='pickupBranchId'
                          label='Çıkış Ofisi'
                          defaultValue={personnel?.branch || ''}
                          wrapperClassName='col-md-3'
                          options={branchOptions}
                          onChange={(value) => {
                                const label = branchOptions.find((o) => o.value === value)?.label
                                syncBranchFields(
                                  'pickupBranch',
                                  value ? { value: value as string, label: label ?? '' } : null,
                                  formik.setFieldValue
                                )
                              }} 
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='pickupBranchNote'
                          label='Çıkış Yeri Notu'
                          wrapperClassName='col-md-3'
                          readOnly={isEditMode}
                        />
                        <SelectInput
                          name='calculationType'
                          label='Hesaplama Tipi'
                          wrapperClassName='col-md-3'
                          options={CALCULATION_TYPES}
                          onChange={(value) =>
                            syncCalculationType(value as string | '0', formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                        <SelectInput
                          name='currency'
                          label='Döviz'
                          wrapperClassName='col-md-3'
                          options={currencyOptions}
                          onChange={(value) =>
                            syncCurrency('currency', value as string | '1', formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='returnBranchId'
                          label='Dönüş Ofisi'
                          defaultValue={personnel?.branch || ''}
                          wrapperClassName='col-md-3'
                          options={branchOptions}  
                          onChange={(value) => {
                                const label = branchOptions.find((o) => o.value === value)?.label
                                syncBranchFields(
                                  'returnBranch',
                                  value ? { value: value as string, label: label ?? '' } : null,
                                  formik.setFieldValue
                                )
                              }} 
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='returnBranchNote'
                          label='Dönüş Yeri Notu'
                          wrapperClassName='col-md-3'
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='price'
                          type={isSelectedResSource ? 'password' : 'text'}
                          readOnly={!isManualPrice || isEditMode}
                          currency={true}
                          label='Fiyat'
                          wrapperClassName='col-md-2'
                          onChange={() => {
                            setIsManualPrice(true)
                          }}
                        />
                        <TextInput name='discount' label='İndirim' readOnly={!isManualPrice || isEditMode} wrapperClassName='col-md-2' />
                        <TextInput
                          name='priceAfterDiscount'
                          currency={true}
                          label='İnd. Sonrası Fiyat'
                          readOnly={true}
                          wrapperClassName='col-md-2'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='vehicleGroupName'
                          label='Araç Grubu'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncReservationSourceModel>
                            name='reservationSource'
                            label='Rezervasyon Kaynağı'
                            placeholder='Rezervasyon kaynağı adı girin...'
                            fetchHandler={fetchAsyncReservationSourceData}
                            displayField={(item) => `${item.reservationSourceName}`}
                            onSelect={(item) => syncReservationSource(item, formik.setFieldValue)}
                            onClear={() => syncReservationSource(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                          />
                        ) : (
                          <TextInput
                            name='reservationSourceName'
                            label='Rezervasyon Kaynağı'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                      </div>
                      <div className='row mb-2'>
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncCarModel>
                            name='car'
                            label='Araç Ara'
                            placeholder='Araç plaka girin...'
                            fetchHandler={fetchAsyncCarData}
                            displayField={(item) => `${item.plate}/${item.brand}/${item.modelType}/${item.transmission}/${item.fuelType}/${item.modelYear}`}
                            onSelect={(item) => syncCarFields(item, formik.setFieldValue)}
                            onClear={() => syncCarFields(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='carPlate'
                            label='Araç Plaka'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                        <SelectInput
                          name='deliveredPersonelId'
                          label='Teslim Eden'
                          options={personelOptions}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row g-3'>
                        <div className='col-12 col-lg-6'>
                          <div className='row g-3'>
                            <TextInput
                              name='brandName'
                              label='Marka'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='modelType'
                              label='Tip'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='transmission'
                              label='Vites'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='fuelType'
                              label='Yakıt'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='outKM'
                              type='number'
                              label='Çıkış KM'
                              wrapperClassName='col-12 col-md-6'
                              onValueChange={(value) =>
                                syncOutKM('outKM', value, formik.setFieldValue)
                              }
                              readOnly={isEditMode}
                            />
                            <SelectInput
                              name='outFuelLevel'
                              label='Çıkış Yakıt'
                              wrapperClassName='col-12 col-md-6'
                              options={FUEL_LEVEL}
                              onChange={(value) =>
                                syncOutFuelLevel('outFuelLevel', value as string, formik.setFieldValue)
                              }
                              disabled={isEditMode}
                            />
                          </div>
                        </div>
                        <div className='col-12 col-lg-6'>
                          <div className='row g-3'>
                            <TextInput
                              name='rentalAmount'
                              currency={true}
                              label='Kira Tutarı (TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='additionalServiceAmount'
                              currency={true}
                              label='Ek Hiz.Tut.(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='totalAmount'
                              currency={true}
                              label='Top. Tutar(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='taxAmount'
                              currency={true}
                              label='Kdv Tutarı (TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='subTotalAmount'
                              currency={true}
                              label='Toplam(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='generalTotalWithCurrency'
                              currency={true}
                              label='Toplam(Döviz)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='remainingAmount'
                              currency={true}
                              label='Kalan'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <SelectInput
                              name='remainingCurrency'
                              label='Kalan Döviz'
                              disabled={isEditMode}
                              options={currencyOptions}
                              wrapperClassName='col-6 col-xl-3'
                              onChange={(value) =>
                                syncCurrency(
                                  'remainingCurrency',
                                  value as string | '1',
                                  formik.setFieldValue
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className='badge-container'>
                        <span className='custom-badge'>
                          <span className='label'>Rez. Kaynağı</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Müşteri</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Tahsilat</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Kalan</span>
                          <span className='value'>
                            {remainingAmount} {currency}
                          </span>
                        </span>
                      </div>

                      <TabNavigationButtons onNext={() => next('res')} />
                    </>
                  )}

                  {tab === 'res' && (
                    <>
                      <div className='row mb-2'>
                        <SelectInput
                          name='operationBranch'
                          label='İşlem Şube'
                          wrapperClassName='col-md-6'
                          options={branchOptions}
                          defaultValue={personnel?.branch}
                          disabled={isEditMode}
                        />
                        <div className='col-md-6'>
                          <div className='d-flex gap-2'>
                            <SelectInput
                              name='optionType'
                              label='Opsiyon Tipi'
                              wrapperClassName='flex-fill'
                              options={OPTION_TYPES}
                              disabled={isEditMode}
                            />
                            <TextInput
                              name='optionDay'
                              label='Gün'
                              type='number'
                              wrapperClassName='flex-fill'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='optionDate'
                              type='date'
                              label='Tarih'
                              wrapperClassName='flex-fill'
                              readOnly={isEditMode}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='reservedPersonelId'
                          label='Rezervasyonu Alan'
                          wrapperClassName='col-md-6'
                          options={personelOptions}
                          defaultValue={personnel?.id ?? undefined}
                          disabled={isEditMode}
                        />
                        <DateInput name='billPeriod' label='Fatura Tarihi' wrapperClassName='col-md-3' readOnly={isEditMode} />
                        <SelectInput
                          name='billPeriodType'
                          label='Fatura Periyodu'
                          wrapperClassName='col-md-3'
                          options={BILL_PERIOD}
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <div className='col-md-6'></div>
                        <SelectInput
                          name='billProfile'
                          label='Fatura Profili'
                          wrapperClassName='col-md-6'
                          options={BILL_PROFILE}
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <DateInput
                          name='startDateDetail'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='col-md-4'
                          onDateChange={(date) =>
                            syncStartDate('startDateDetail', date, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='startTimeDetail'
                          type='time'
                          label='Başlangıç Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('startTimeDetail', time, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='giftDay'
                          type='number'
                          label='Hediye Gün'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='rentalDayDetail'
                          type='number'
                          label='Gün'
                          readOnly={true}
                          wrapperClassName='col-md-1'
                        />
                        <DateInput
                          name='endDateDetail'
                          type='date'
                          label='Beklenen Tarih'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('endDateDetail', date, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='endTimeDetail'
                          type='time'
                          label='Beklenen Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('endTimeDetail', time, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('quick')} onNext={() => next('customer')} />
                    </>
                  )}

                  {tab === 'customer' && (
                    <>
                      <div className='row mb-2'>
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncCustomerModel>
                            name='customer1'
                            label='Cari Ara'
                            placeholder='Ad Soyad girin...'
                            fetchHandler={fetchAsyncCustomerData}
                            displayField={(item) => `${item.firstName} ${item.lastName}`}
                            onSelect={(item) => syncCustomerFields(item, formik.setFieldValue)}
                            onClear={() => syncCustomerFields(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='customerFullName'
                            label='Cari Adı Soyadı'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                        <TextInput
                          name='driverLicenseNo'
                          label='Ehliyet No'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <PhoneInput name='phone' label='Cep Tel' wrapperClassName='col-md-6' readOnly={isEditMode} />
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncReservationSourceModel>
                            name='reservationSourceDetail'
                            label='Rezervasyon Kaynağı 1'
                            placeholder='Kayıtlı bir rezervasyon kaynağı arayın ve seçin'
                            fetchHandler={fetchAsyncReservationSourceData}
                            displayField={(item) => `${item.reservationSourceName}`}
                            onSelect={(item) => syncReservationSource(item, formik.setFieldValue)}
                            onClear={() => syncReservationSource(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='reservationSourceName'
                            label='Rezervasyon Kaynağı 1'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='email'
                          type='email'
                          label='Mail Adresi'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncReservationSourceModel>
                            name='secondReservationSource'
                            label='Rezervasyon Kaynağı 2'
                            placeholder='Kayıtlı bir rezervasyon kaynağı arayın ve seçin'
                            fetchHandler={fetchAsyncReservationSourceData}
                            displayField={(item) => `${item.reservationSourceName}`}
                            onSelect={(item) =>
                              formik.setFieldValue('secondReservationSourceId', item.id)
                            }
                            onClear={() => formik.setFieldValue('secondReservationSourceId', '')}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='secondReservationSourceName'
                            label='Rezervasyon Kaynağı 2'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='tcIdentityNo'
                          label='TC Kimlik No'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='licensePlace'
                          label='Ehliyet Yer'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='district' label='İlçe' wrapperClassName='col-md-6' readOnly={isEditMode} />
                        <TextInput
                          name='licenseDate'
                          label='Ehliyet Tarih'
                          type='date'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='city' label='Şehir' wrapperClassName='col-md-6' readOnly={isEditMode} />
                        <TextInput
                          name='birthPlace'
                          label='Doğum Yeri'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='country' label='Ülke' wrapperClassName='col-md-6' readOnly={isEditMode} />
                        <TextInput
                          name='birthday'
                          label='Doğum Tarihi'
                          type='date'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='totalRentalCount'
                          label='Toplam Kira Adeti'
                          type='number'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='passportNo'
                          label='Pasaport No'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextAreaInput name='address' label='Adres' wrapperClassName='col-md-6' readOnly={isEditMode} />
                        <TextInput
                          name='passportPlace'
                          label='Pasaport Yeri'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('res')} onNext={() => next('car')} />
                    </>
                  )}

                  {tab === 'car' && (
                    <>
                      <div className='row mb-2'>
                        {!isEditMode ? (
                          <AsyncSearchInput<AsyncCarModel>
                            name='carDetail'
                            label='Araç Ara'
                            placeholder='Araç plaka giriniz...'
                            fetchHandler={fetchAsyncCarData}
                            displayField={(car) => `${car.plate} (${car.modelYear})`}
                            onSelect={(item) => syncCarFields(item, formik.setFieldValue)}
                            onClear={() => syncCarFields(null, formik.setFieldValue)}
                            wrapperClassName='col-md-6'
                          />) : (
                          <TextInput
                            name='carPlate'
                            label='Araç Plaka'
                            readOnly={true}
                            wrapperClassName='col-md-6'
                          />
                        )}
                        <TextInput
                          name='brandNameDetail'
                          label='Marka'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='modelTypeDetail'
                          label='Tipi'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                        <TextInput
                          name='modelYear'
                          label='Yılı'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='transmissionDetail'
                          label='Vites'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                        <TextInput
                          name='fuelTypeDetail'
                          label='Yakıt Türü'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='vehicleGroupNameDetail'
                          label='Araç Grubu'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='outKmDetail'
                          type='number'
                          label='Çıkış KM'
                          wrapperClassName='col-md-6'
                          onValueChange={(value) =>
                            syncOutKM('outKmDetail', value, formik.setFieldValue)
                          }
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='reservationGroup'
                          label='Rezervasyon Grubu'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='outFuelLevelDetail'
                          label='Çıkış Yakıt'
                          wrapperClassName='col-md-6'
                          options={FUEL_LEVEL}
                          onChange={(value) =>
                            syncOutFuelLevel('outFuelLevelDetail', value as string, formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='carLocation'
                          label='Araç Yeri'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='fullnessAccount'
                          label='Doluluk Hesap'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('customer')} onNext={() => next('price')} />
                    </>
                  )}

                  {tab === 'price' && (
                    <>
                      <div className='row mb-2'>
                        <SelectInput
                          name='pricingType'
                          label='Fiyatlandırma'
                          options={CALCULATION_TYPES}
                          wrapperClassName='col-md-6'
                          onChange={(value) =>
                            syncCalculationType(value as string | '0', formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='rentalDay'
                          label='Kira Gün'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='price1'
                          type={isSelectedResSource ? 'password' : 'text'}
                          readOnly={!isManualPrice || isEditMode}
                          currency={true}
                          label='Fiyat'
                          wrapperClassName='col-md-2'
                          onChange={() => {
                            setIsManualPrice(true)
                          }}
                        />
                        <TextInput
                          name='discount1'
                          type='number'
                          label='İndirim'
                          readOnly={!isManualPrice || isEditMode}
                          wrapperClassName='col-md-2'
                        />
                        <TextInput
                          name='priceAfterDiscount1'
                          currency={true}
                          label='İnd. Sonrası Fiyat'
                          readOnly={true}
                          wrapperClassName='col-md-2'
                        />
                        <SelectInput
                          name='currency1'
                          label='Döviz'
                          options={currencyOptions}
                          wrapperClassName='col-md-6'
                          onChange={(value) =>
                            syncCurrency('currency1', value as string | '1', formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='autoDesc'
                          label='Otomatik Açıklama'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='campaign'
                          label='Kampanya'
                          options={[{ label: 'Örnek Kampanya', value: '0' }]}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='deliveryType'
                          label='Teslimat Şekli'
                          options={DELIVERY_TYPES}
                          wrapperClassName='col-md-6'
                          onChange={(value) =>
                            syncDeliveryType(value as string | '0', formik.setFieldValue)
                          }
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='campaignCondition'
                          label='Kampanya Şartı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='tariffId'
                          label='Tarife'
                          options={tariffOptions}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='customVatRate'
                          type='number'
                          label='Özel KDV Oranı'
                          wrapperClassName='col-md-6'
                          readOnly={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='rentAmount1'
                          label='Kira Tutarı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='totalAmount1'
                          label='Toplam Tutar'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='serviceAmount1'
                          label='Hizmet Tutarı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='kdvTl1'
                          label='KDV'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='grandTotal'
                          label='Genel Toplam'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='promotionCode'
                          label='Promosyon Kodu'
                          wrapperClassName='col-md-2'
                          readOnly={isEditMode}
                        />
                        <TextInput
                          name='promotionAmount'
                          label='Tutar'
                          wrapperClassName='col-md-2'
                          readOnly={isEditMode}
                        />
                        <SelectInput
                          name='promotionCurrency'
                          label='Döviz'
                          options={currencyOptions}
                          wrapperClassName='col-md-2'
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='collection'
                          label='Tahsilat'
                          readOnly={true}
                          currency={true}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='paymentMethod'
                          label='Ödeme Şekli'
                          options={PAYMENT_TYPES}
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                        <TextInput
                          name='remaining1'
                          label='Kalan'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                          disabled={isEditMode}
                        />
                        <SelectInput
                          name='remainingCurrency1'
                          label='Kalan Döviz'
                          disabled={isEditMode}
                          options={currencyOptions}
                          wrapperClassName='col-md-3'
                          onChange={(value) =>
                            syncCurrency(
                              'remainingCurrency1',
                              value as string | '1',
                              formik.setFieldValue
                            )
                          }
                        />
                      </div>
                      <div className='row mb-2'>
                        <CheckboxInput
                          name='hourDifferenceTaken'
                          label='Saat Farkını Alma'
                          wrapperClassName='col-md-6'
                          disabled={isEditMode}
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('car')} onNext={() => next('extra')} />
                    </>
                  )}

                  {tab === 'extra' && (
                    <>
                      <div className='mb-5'>
                        <div className='row mb-2'>
                          <TextInput
                            name='customerAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                          <TextInput
                            name='pickupBranchAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='returnBranchAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='priceAdd'
                            readOnly={true}
                            currency={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='generalCurrencyAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='startDateAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='startTimeAdd'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='rentDayAdd'
                            readOnly={true}
                            wrapperClassName='col-md-1'
                          />
                          <TextInput name='plateAdd' readOnly={true} wrapperClassName='col-md-3' />
                          <TextInput
                            name='calculationTypeAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='endDateAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='endTimeAdd'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='vehicleGroupAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                          <div className='col-md-4'>
                            <div className='row mb-2'>
                              <TextInput
                                name='totalAmountAdd'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-4'
                              />
                              <TextInput
                                name='remainingAmountAdd'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-5'
                              />
                              <TextInput
                                name='currencyAdd'
                                readOnly={true}
                                wrapperClassName='col-md-3'
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='additionalCurrencyAdd'
                          label='Hizmet Döviz'
                          wrapperClassName='col-md-4'
                          options={currencyOptions}
                          labelPosition='left'
                          disabled={isEditMode}
                        />
                        <SelectInput
                          name='additionalServiceSellOwner'
                          label='Hizmet Satan'
                          wrapperClassName='col-md-5'
                          firstItemLabel='Ek Hizmet Satanı Seçiniz'
                          options={personelOptions}
                          labelPosition='left'
                          disabled={isEditMode}
                        />
                        <CheckboxInput
                          name='fullCredit'
                          label='Full Credit'
                          wrapperClassName='col-md-3'
                          disabled={isEditMode}
                        />
                      </div>
                      {formik.values.additionalServices?.map((service, index) => {
                        const calculateTotal = (
                          index: number,
                          changedField?: 'piece' | 'price',
                          newValue?: string | null
                        ) => {
                          const service = formik.values.additionalServices![index]

                          const pieceValue =
                            changedField === 'piece'
                              ? Number(newValue ?? 0)
                              : Number(service.piece || 0)

                          const priceValue =
                            changedField === 'price'
                              ? Number(newValue ?? 0)
                              : Number(service.price || 0)

                          const total = service.hasPiece ? pieceValue * priceValue : priceValue

                          formik.setFieldValue(
                            `additionalServices[${index}].totalPrice`,
                            Number(total.toFixed(2)),
                            false
                          )
                        }

                        return (
                          <div className='row mb-6' key={service.name}>
                            <CheckboxInput
                              name={`additionalServices[${index}].isAdd`}
                              label={service.title}
                              wrapperClassName={
                                service.hasPiece
                                  ? service.hasProduct
                                    ? 'col-md-3 pt-8'
                                    : 'col-md-3'
                                  : service.hasProduct
                                    ? 'col-md-4 pt-8'
                                    : 'col-md-4'
                              }
                            />
                            {service.hasPiece && (
                              <TextInput
                                name={`additionalServices[${index}].piece`}
                                type='number'
                                label={service.hasProduct ? 'Adet' : ''}
                                wrapperClassName='col-md-1 ps-0'
                                onValueChange={(value) =>
                                  service.isDaily && calculateTotal(index, 'piece', value)
                                }
                              />
                            )}
                            <div className='col-md-5'>
                              <div className='row'>
                                {service.isDaily ? (
                                  <TextInput
                                    name={`additionalServices[${index}].price`}
                                    currency={true}
                                    label={service.hasProduct ? 'Günlük' : ''}
                                    labelPosition='top'
                                    wrapperClassName='col-md-4 ps-0'
                                    onValueChange={(value) => calculateTotal(index, 'price', value)}
                                  />
                                ) : (
                                  <div className='col-md-4 ps-0' />
                                )}

                                {service.hasProduct ? (
                                  <>
                                    <TextInput
                                      name={`additionalServices[${index}].productNumber`}
                                      placeholder='Koltuk Etiketi'
                                      wrapperClassName='col-md-4 pt-8 ps-0'
                                    />
                                    <SelectInput
                                      name={`additionalServices[${index}].productOwner`}
                                      wrapperClassName='col-md-4 pt-8 ps-0'
                                      options={PRODUCT_OWNER}
                                      defaultValue='0'
                                    />
                                  </>
                                ) : (
                                  <>
                                    <div className='col-md-2 ps-0' />
                                    <div className='col-md-2 ps-0' />
                                  </>
                                )}
                              </div>
                            </div>
                            <div className='col-md-3'>
                              <div className='row'>
                                <TextInput
                                  name={`additionalServices[${index}].totalPrice`}
                                  label={service.hasProduct ? 'Toplam Bedel' : ''}
                                  currency={true}
                                  wrapperClassName='col-md-7 ps-0'
                                  readOnly={service.isDaily}
                                />
                                <SelectInput
                                  name={`additionalServices[${index}].status`}
                                  label={service.hasProduct ? 'Durum' : ''}
                                  wrapperClassName='col-md-5 ps-0'
                                  options={ADDITIONAL_SERVICE_STATUS}
                                  defaultValue='0'
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      <TabNavigationButtons onBack={() => back('price')} onNext={() => next('details')} />
                    </>
                  )}

                  {tab === 'details' && (
                    <>
                      <SubTabHeader tab={tabSub} setTab={setTabSub} tabs={rentSubTabOptions} />
                      {tabSub === 'desc' && (
                        <>
                          <TextAreaInput
                            name='description'
                            label='Açıklama'
                            wrapperClassName='mb-12'
                            readOnly={isEditMode}
                          />
                          <TextAreaInput
                            name='warningDescription'
                            label='Uyarı Açıklama'
                            wrapperClassName='mb-12'
                            readOnly={isEditMode}
                          />
                          <TextAreaInput
                            name='billDescription'
                            label='Özel Fatura Açıklaması'
                            wrapperClassName='mb-12'
                            readOnly={isEditMode}
                          />
                        </>
                      )}
                      {tabSub === 'flight' && (
                        <>
                          <div className='row mb-2'>
                            <TextInput
                              name='accountOwner'
                              label='Hesap Sahibi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='provisionNo'
                              label='Provizyon No'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='accountNo'
                              label='No'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='provisionAmount'
                              label='Provizyon Tutarı'
                              currency={true}
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <DateInput
                              name='accountDate'
                              type='date'
                              label='Tarih'
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='provisionDate'
                              type='date'
                              label='Provizyon Tarihi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='security'
                              label='Güvenlik'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                              />
                          </div>
                        </>
                      )}
                      {tabSub === 'drivers' && (
                        <> 
                          <div className='row mb-2'>
                            {!isEditMode ? (
                            <AsyncSearchInput<AsyncCustomerModel>
                              name='driverFirst.driver'
                              label='1. Sürücüyü Ara'
                              placeholder='Ad Soyad girin...'
                              fetchHandler={fetchAsyncCustomerData}
                              displayField={(item) => `${item.firstName} ${item.lastName}`}
                              onSelect={(item) =>
                                setDriverFields('driverFirst', item, formik.setFieldValue)
                              }
                              onClear={() =>
                                setDriverFields('driverFirst', null, formik.setFieldValue)
                              }
                              wrapperClassName='col-md-6'
                            />) : (
                              <TextInput
                                name='driverFirst.fullName'
                                label='1. Sürücü'
                                wrapperClassName='col-md-6'
                                readOnly={true}
                              />
                            )}
                            <TextInput
                              name='driverFirst.phone'
                              label='Telefon'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.tckn'
                              label='TC No'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='driverFirst.licenseNo'
                              label='Ehliyet No'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.name'
                              label='Ad'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverFirst.licensePlace'
                              label='Ehliyet Veren Yer'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.surname'
                              label='Soyad'
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='driverFirst.licenseDate'
                              type='date'
                              label='Ehliyet Veriliş Tarihi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <DateInput
                              name='driverFirst.birthDate'
                              type='date'
                              label='Doğum Tarihi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          {/* 2. Sürücü */}
                          <div className='row mb-2'>
                            {!isEditMode ? (
                            <AsyncSearchInput<AsyncCustomerModel>
                              name='driverSecond.driver'
                              label='2. Sürücüyü Ara'
                              placeholder='Ad Soyad girin...'
                              fetchHandler={fetchAsyncCustomerData}
                              displayField={(item) => `${item.firstName} ${item.lastName}`}
                              onSelect={(item) =>
                                setDriverFields('driverSecond', item, formik.setFieldValue)
                              }
                              onClear={() =>
                                setDriverFields('driverSecond', null, formik.setFieldValue)
                              }
                              wrapperClassName='col-md-6'
                            />
                            ) : (
                              <TextInput
                                name='driverSecond.fullName'
                                label='2. Sürücü'
                                wrapperClassName='col-md-6'
                                readOnly={true}
                              />
                            )}
                            <TextInput
                              name='driverSecond.phone'
                              label='Telefon'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.tckn'
                              label='TC No'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='driverSecond.licenseNo'
                              label='Ehliyet No'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.name'
                              label='Ad'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='driverSecond.licensePlace'
                              label='Ehliyet Veren Yer'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.surname'
                              label='Soyad'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <DateInput
                              name='driverSecond.licenseDate'
                              type='date'
                              label='Ehliyet Veriliş Tarihi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <DateInput
                              name='driverSecond.birthDate'
                              type='date'
                              label='Doğum Tarihi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'other' && (
                        <>
                          <div className='row mb-2'>
                            <TextInput
                              name='manualFindexScore'
                              label='Manuel Findex Puanı'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='projectName'
                              label='Proje Adı'
                              readOnly={isEditMode}
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='customerVehicle'
                              label='Müşteri Araç'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <SelectInput
                              name='riskPermissionLevel'
                              label='Risk İzinli'
                              wrapperClassName='col-md-6'
                              options={RISK_LEVEL}
                              disabled={isEditMode}
                              />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='vehicleDetails'
                              label='Araç Detayı'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                            <TextInput
                              name='guarantor'
                              label='Kefil'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='requestType'
                              label='Talep Türü'
                              readOnly={true}
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='specialDriverInfo'
                              label='Özel Şöför Bilgisi'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='requestSourceUnit'
                              label='Talep Geldiği Birim'
                              readOnly={true}
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='specialDriverCode'
                              label='Özel Şöför Kodu'
                              readOnly={true}
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='companyCode'
                              label='Firma Kodu'
                              readOnly={true}
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='specialCode'
                              label='Özel Kod'
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='approvalCode'
                              label='Onay Kodu'
                              readOnly={true}
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-2'>
                            <CheckboxInput
                              name='noShowBillList'
                              label='Fatura Listesinde Gösterme'
                              wrapperClassName='col-md-6'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='kabisOut'
                              label='Kabis Çıkış Yapıldı'
                              wrapperClassName='col-md-6'
                              disabled={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <CheckboxInput
                              name='automaticExtending'
                              label='Otomatik Uzatma'
                              wrapperClassName='col-md-6'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='kabisReturn'
                              label='Kabis Dönüş Yapıldı'
                              wrapperClassName='col-md-6'
                              disabled={isEditMode}
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'accessory' && (
                        <>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Yedek Anahtar</label>
                            </div>
                            <CheckboxInput
                              name='spareKeyBefore'
                              label='Önce'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='spareKeyAfter'
                              label='Sonra'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Stepne</label>
                            </div>
                            <CheckboxInput
                              name='spareTireBefore'
                              label='Önce'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='spareTireAfter'
                              label='Sonra'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Zincir</label>
                            </div>
                            <CheckboxInput
                              name='chainBefore'
                              label='Önce'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='chainAfter'
                              label='Sonra'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>İlk Yardım Çantası</label>
                            </div>
                            <CheckboxInput
                              name='firstAidBagBefore'
                              label='Önce'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                            <CheckboxInput
                              name='firstAidBagAfter'
                              label='Sonra'
                              wrapperClassName='col-md-5'
                              disabled={isEditMode}
                            />
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Ön Sol Lastik</label>
                            </div>
                            <div className='col-md-10'>
                              <div className='row mb-2'>
                                <TextInput
                                  name='frontLeftTireBefore'
                                  label='Önce'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  disabled={isEditMode}
                                />
                                <TextInput
                                  name='frontLeftTireAfter'
                                  label='Sonra'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  disabled={isEditMode}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Ön Sağ Lastik</label>
                            </div>
                            <div className='col-md-10'>
                              <div className='row mb-2'>
                                <TextInput
                                  name='frontRightTireBefore'
                                  label='Önce'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                                <TextInput
                                  name='frontRightTireAfter'
                                  label='Sonra'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Arka Sol Lastik</label>
                            </div>
                            <div className='col-md-10'>
                              <div className='row mb-2'>
                                <TextInput
                                  name='backLeftTireBefore'
                                  label='Önce'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                                <TextInput
                                  name='backLeftTireAfter'
                                  label='Sonra'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='row mb-2'>
                            <div className='col-md-2'>
                              <label className='form-label fw-bold mb-0'>Arka Sağ Lastik</label>
                            </div>
                            <div className='col-md-10'>
                              <div className='row mb-2'>
                                <TextInput
                                  name='backRightTireBefore'
                                  label='Önce'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                                <TextInput
                                  name='backRightTireAfter'
                                  label='Sonra'
                                  labelPosition='left'
                                  wrapperClassName='col-md-6'
                                  readOnly={isEditMode}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      <TabNavigationButtons onBack={() => back('extra')} onNext={() => next('return')} />
                    </>
                  )}

                  {tab === 'return' && (
                    <>
                      <div className='mb-5'>
                        <div className='row mb-2'>
                          <TextInput
                            name='customerReturn'
                            readOnly={true}
                            wrapperClassName='col-md-4' 
                          />
                          <TextInput
                            name='pickupBranchReturn'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='returnBranchReturn'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='priceReturn'
                            readOnly={true}
                            currency={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='generalCurrencyReturn'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='startDateReturn'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='startTimeReturn'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='rentDayReturn'
                            readOnly={true}
                            wrapperClassName='col-md-1'
                          />
                          <TextInput name='plateReturn' readOnly={true} wrapperClassName='col-md-3' />
                          <TextInput
                            name='calculationTypeReturn'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='endDateReturn'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='endTimeReturn'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='vehicleGroupReturn'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                          <div className='col-md-4'>
                            <div className='row mb-2'>
                              <TextInput
                                name='totalAmountReturn'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-4'
                              />
                              <TextInput
                                name='remainingAmountReturn'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-5'
                              />
                              <TextInput
                                name='currencyReturn'
                                readOnly={true}
                                wrapperClassName='col-md-3'
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='statusReturn'
                          label='Durum'
                          wrapperClassName='col-md-3'
                          options={RENT_STATUS} 
                        />
                        <div className='col-md-3'></div>
                        <TextInput
                          name='outKMReturn'
                          type='number'
                          label='Çıkış KM'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                        <SelectInput
                          name='outFuelLevelReturn'
                          label='Çıkış Yakıt'
                          disabled={true}
                          wrapperClassName='col-md-3'
                          options={FUEL_LEVEL}
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='returnDateText'
                          label='Dönüş Tarihi'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='returnTimeText'
                          label='Dönüş Saati'
                          readOnly={true}
                          wrapperClassName='col-md-2'
                        />
                        <div className='col-md-1 pt-8'>
                          <Button
                            type='button'
                            className='btn btn-primary btn-active-light-primary'
                          >
                            <i className='bi bi-calendar fs-2'></i>
                          </Button>
                        </div>
                        <TextInput
                          name='inKM'
                          type='number'
                          label='Dönüş KM'
                          wrapperClassName='col-md-3'
                          onValueChange={(value) => calculateUseKM(Number(formik.values.outKMReturn) || 0, Number(value) || 0, formik.setFieldValue)} 
                        />
                        <TextInput
                          name='usedKM'
                          label='Kullanılan KM'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='recipientPersonelId'
                          label='Teslim Alan'
                          options={personelOptions}
                          wrapperClassName='col-md-6' 
                        />
                        <CheckboxInput
                          name='overKmManuel'
                          label='Km Günlük/Toplam Sınır/Fark'
                          wrapperClassName='col-md-3 mt-8' 
                        />
                        <div className='col-md-3 mt-8'>
                          <div className='row mb-2'>
                            <TextInput name='dailyKmLimit' type='number' wrapperClassName='col-md-4' readOnly={true}/>
                            <TextInput name='totalKmLimit' type='number' wrapperClassName='col-md-4' readOnly={true}/>
                            <TextInput
                              name='differenceKM'
                              type='number'
                              readOnly={true}
                              wrapperClassName='col-md-4'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='deliveryTypeReturn'
                          label='Teslimat Şekli'
                          options={DELIVERY_TYPES}
                          wrapperClassName='col-md-6'
                          onChange={(value) => syncDeliveryType(value as string | '0', formik.setFieldValue)} 
                        />
                        <CheckboxInput
                          name='overKmFeeManuel'
                          label='Km Aşım Bedeli'
                          wrapperClassName='col-md-3 mt-8' 
                        />
                        <div className='col-md-3 mt-8'>
                          <div className='row mb-2'>
                            <TextInput
                              name='dailyOverKmFee'
                              currency={true}
                              wrapperClassName='col-md-6' 
                            />
                            <TextInput
                              name='totalOverKmFee'
                              currency={true}
                              wrapperClassName='col-md-6'
                              readOnly={isEditMode}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='returnReason'
                          label='Bitiş Sebebi'
                          options={RETURN_REASON}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='inFuelLevel'
                          label='Dönüş Yakıt'
                          wrapperClassName='col-md-3'
                          options={FUEL_LEVEL}
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('details')} />
                    </>
                  )}
                </Form>
              </div>
            </>
          )
        }}
      </Formik>

      <CustomerCreateModal
        show={showCustomerModal}
        modalId='modal_customer_search'
        handleClose={() => setShowCustomerModal(false)}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
